import { Grid } from '@mui/material';
import { BaseButton } from 'components/BaseButton';
import TextField from 'components/text-field';
import React, { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ContainerModal from './modal-container';
import { useMutation } from 'react-query';
import graphql from 'services/querys';
import { NewAddressType } from 'types/new-user';
import { createPartyPayment } from 'graphql/wire-transfers/mutations';
import { useTranslation } from 'react-i18next';

type DialogAddressProps = {
  open: boolean;
  onClose: () => void;
  partyId: string;
  onSuccess: (addresses: NewAddressType[]) => void;
};

export type CreateWireTransferForm = {
  account: string;
  bank: string;
  bank_address: string;
  country: string;
  name: string;
  routing: string;
};

const CreateAccountModal: FC<DialogAddressProps> = ({ open, onClose, partyId, onSuccess }) => {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<CreateWireTransferForm>({
    defaultValues: {
      account: '',
      bank: '',
      bank_address: '',
      country: 'US',
      name: '',
      routing: '',
    },
  });
  const { mutate: createPayment, isLoading: isCreatePaymentLoading } = useMutation(
    createPartyPayment,
    {
      onSuccess: (response) => {
        onClose();
      },
    }
  );

  return (
    <ContainerModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSave={() => {
        handleSubmit((data) => {
          createPayment({
            partyId,
            dataWire: data,
          });
        })();
      }}
      disableSavebutton={!formState.isValid}
      loadingSaveButton={isCreatePaymentLoading}
      modalTitle={t('contact:addAccountModalTitle')}
    >
      <form>
        <Grid container direction="column" spacing={2}>
          <Grid item container direction="row" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="account"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:accountNumberLabel')}
                    name="account"
                    disabled={isCreatePaymentLoading}
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="bank"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:bankLabel')}
                    name="bank"
                    disabled={isCreatePaymentLoading}
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid item container direction="row" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="bank_address"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:bankAddressLabel')}
                    name="bank-address"
                    disabled={isCreatePaymentLoading}
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="country"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:countryLabel')}
                    name="country"
                    disabled
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid item container direction="row" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="name"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:accountNameLabel')}
                    name="name"
                    disabled={isCreatePaymentLoading}
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="routing"
                rules={{ required: true }}
                render={({ field }) => (
                  <TextField
                    label={t('contact:routingNumberLabel')}
                    name="routing"
                    disabled={isCreatePaymentLoading}
                    handleBlur={field.onBlur}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </ContainerModal>
  );
};

export default CreateAccountModal;
